/**
 * Zod schemas + DTO types for the Absence API surface — Phase 3 (W1.T2).
 *
 * Decisions:
 *   - D01: enum is exactly { VACATION, SICK_LEAVE } — NO `OTHER` in M1.
 *     M2 may add additive enum values via a separate migration; M1 stays
 *     conservative because the wider taxonomy is still under HR review.
 *   - D05a: self-edit only — service layer asserts `actor.id === existing.employeeId`.
 *   - dateFrom <= dateTo: enforced server-side by Postgres CHECK constraint
 *     (migration 010, gate 1). FE schemas keep ONLY the syntactic shape so
 *     the BE remains the single source of truth on calendar semantics
 *     (mirrors the workload date approach).
 *
 * All mutating schemas are `.strict()` so `employeeId` smuggled in the body
 * is rejected — actor.id is always taken from the JWT.
 */
import { z } from 'zod';
import { WorkloadDateSchema } from './workload';
import { ruEnum } from './lib/zod-ru';

// ─── Enum ─────────────────────────────────────────────────────────────────

/**
 * Absence kind. Matches Prisma `ABSENCE_KIND` enum (migration 010).
 *
 * NB: do NOT add `OTHER` here — D01 explicitly excludes it. If a new value
 * is needed in M2, mirror it here AND in `prisma/schema.prisma` AND ship
 * an additive enum migration.
 */
export const AbsenceKindEnum = z.enum(
  ['VACATION', 'SICK_LEAVE'],
  ruEnum('Выберите вид отсутствия'),
);
export type AbsenceKind = z.infer<typeof AbsenceKindEnum>;

/** Notes — same 2000-char ceiling as workload comment / reportText. */
export const AbsenceNotesSchema = z.string().max(2000, {
  message: 'Заметка не должна превышать 2000 символов',
});

const UuidSchema = z.string().uuid();

/**
 * Maximum span (inclusive) of an absence window. DA-4 fix.
 *
 * ⚠️ ОСНОВАНИЕ ОТПАЛО ЧАСТИЧНО, САМ ПОТОЛОК — НЕТ. Прежний довод:
 * «ограничивает SERIALIZABLE-транзакцию планового каскада одним годом,
 * чтобы опечатка 2026..2030 не подвесила базу окном в 1825 дней».
 * Каскада нет с волны Ф4 · В10-А2 (Р-26) — довода про транзакцию больше
 * не существует. Потолок СОХРАНЁН на своём, более простом основании:
 * это проверка ввода, отсекающая заведомую опечатку в датах, и она от
 * каскада никогда не зависела. M1 reality: VACATION rarely
 * exceeds 28 days; SICK_LEAVE rarely exceeds 60. 365 days is a generous
 * ceiling (reflects max consecutive medical leave per RU labour code).
 */
const ABSENCE_MAX_SPAN_DAYS = 365;

/** Days between two YYYY-MM-DD strings, inclusive of both ends. */
function absenceDaysBetween(fromIso: string, toIso: string): number {
  const a = Date.parse(`${fromIso}T00:00:00Z`);
  const b = Date.parse(`${toIso}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return Number.POSITIVE_INFINITY;
  return Math.floor((b - a) / 86_400_000) + 1;
}

// ─── Write requests ───────────────────────────────────────────────────────

/**
 * `POST /absences` (self-create). `employeeId` is taken from the JWT —
 * deliberately absent from the body.
 *
 * Side-effect at the service layer (D12): plan rows that overlap the
 * absence window are deleted per-row (audit fires for each). Fact rows
 * on those dates are preserved (retroactive absence does NOT contradict
 * an already-recorded fact).
 */
export const CreateAbsenceRequestSchema = z
  .object({
    type: AbsenceKindEnum,
    dateFrom: WorkloadDateSchema,
    dateTo: WorkloadDateSchema,
    notes: AbsenceNotesSchema.optional(),
  })
  .strict()
  .refine((dto) => absenceDaysBetween(dto.dateFrom, dto.dateTo) <= ABSENCE_MAX_SPAN_DAYS, {
    message: `Период отсутствия не должен превышать ${ABSENCE_MAX_SPAN_DAYS} дней`,
    path: ['dateTo'],
  });
export type CreateAbsenceRequest = z.infer<typeof CreateAbsenceRequestSchema>;

/**
 * `PATCH /absences/:id` body. `version` REQUIRED (optimistic-lock).
 * Service layer re-runs the plan-purge if `dateFrom` / `dateTo` widen
 * the window beyond the previous range.
 */
export const UpdateAbsenceRequestSchema = z
  .object({
    version: z.number().int().nonnegative(),
    type: AbsenceKindEnum.optional(),
    dateFrom: WorkloadDateSchema.optional(),
    dateTo: WorkloadDateSchema.optional(),
    notes: AbsenceNotesSchema.optional(),
  })
  .strict();
export type UpdateAbsenceRequest = z.infer<typeof UpdateAbsenceRequestSchema>;

// ─── Read queries ─────────────────────────────────────────────────────────

/**
 * `GET /absences` query (manager / admin scopes). Service-layer RBAC:
 *   - self → own
 *   - РО (MANAGER) → own dept only
 *   - ADMIN → any
 *   - SUPERVISOR → 403 (config-only scope per Phase 0).
 */
export const ListAbsencesQuerySchema = z
  .object({
    employeeId: UuidSchema.optional(),
    type: AbsenceKindEnum.optional(),
    /** Window filter — overlaps with [dateFrom, dateTo] on the row. */
    fromDate: WorkloadDateSchema.optional(),
    toDate: WorkloadDateSchema.optional(),
    page: z.coerce.number().int().min(1).max(10_000).optional().default(1),
    limit: z.coerce.number().int().min(1).max(200).optional().default(50),
  })
  .strict();
export type ListAbsencesQuery = z.infer<typeof ListAbsencesQuerySchema>;

/**
 * `GET /absences/active?employeeId=X&date=Y` — used by the workload
 * heatmap to mark cells. Returns the highest-priority overlapping
 * absence (SICK_LEAVE > VACATION, then dateFrom DESC, then createdAt
 * DESC) or `{ absence: null }`.
 */
export const ActiveAbsenceQuerySchema = z
  .object({
    employeeId: UuidSchema,
    date: WorkloadDateSchema,
  })
  .strict();
export type ActiveAbsenceQuery = z.infer<typeof ActiveAbsenceQuerySchema>;

// ─── Response shapes ─────────────────────────────────────────────────────

/** A single Absence row as returned by the API. */
export const AbsenceRowSchema = z.object({
  id: UuidSchema,
  employeeId: UuidSchema,
  type: AbsenceKindEnum,
  dateFrom: WorkloadDateSchema,
  dateTo: WorkloadDateSchema,
  notes: z.string().nullable(),
  version: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AbsenceRow = z.infer<typeof AbsenceRowSchema>;

export const AbsenceListResponseSchema = z.object({
  rows: z.array(AbsenceRowSchema),
  page: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  totalRows: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
});
export type AbsenceListResponse = z.infer<typeof AbsenceListResponseSchema>;

/** Active-absence response — see tiebreaker rule above. */
export const ActiveAbsenceResponseSchema = z.object({
  absence: AbsenceRowSchema.nullable(),
});
export type ActiveAbsenceResponse = z.infer<typeof ActiveAbsenceResponseSchema>;

/**
 * Single-absence create / update response.
 *
 * ⚠️ Здесь стояло поле `deletedPlanRows` — счёт плановых строк, снесённых
 * каскадом D12. Каскад снят волной Ф4 · В10-А2 (решения Р-25, Р-26):
 * отсутствие плановые строки больше не удаляет, и поле убрано, а не
 * обнулено — вечный ноль был бы утверждением, а не пустотой.
 */
export const AbsenceMutationResponseSchema = z.object({
  absence: AbsenceRowSchema,
});
export type AbsenceMutationResponse = z.infer<typeof AbsenceMutationResponseSchema>;
